<template>
  <article class="sale-summary-card sale-summary-card--financial sale-summary-card--expenses-progress">
    <header class="sale-summary-card__header">
      <div>
        <h4>{{ text.title }}</h4>
        <p>{{ text.subtitle }}</p>
      </div>

      <span class="sale-summary-pill" :class="expenseStatus.className">{{ expenseStatus.label }}</span>
    </header>

    <div class="sale-summary-progress">
      <div class="sale-summary-progress__head">
        <span>{{ text.progressTotalLabel }}</span>
        <strong>{{ totalProgressLabel }}</strong>
      </div>

      <div class="sale-summary-progress__track" role="progressbar" :aria-valuenow="progressPercent" aria-valuemin="0" aria-valuemax="100">
        <div class="sale-summary-progress__fill" :style="progressStyle"></div>
      </div>

      <div class="sale-summary-progress__head sale-summary-progress__head--secondary">
        <span>{{ text.progressPaidLabel }}</span>
        <strong>{{ paidProgressLabel }}</strong>
      </div>

      <div class="sale-summary-progress__track" role="progressbar" :aria-valuenow="paidProgressPercent" aria-valuemin="0" aria-valuemax="100">
        <div class="sale-summary-progress__fill sale-summary-progress__fill--paid" :style="paidProgressStyle"></div>
      </div>
    </div>

    <div class="sale-summary-kpis">
      <div class="sale-summary-kpi-box">
        <span>{{ text.kpis.budget }}</span>
        <strong>{{ formatMoney(budgetAmount) }}</strong>
      </div>
      <div class="sale-summary-kpi-box">
        <div class="sale-summary-kpi-lines">
          <small>{{ text.kpis.spentTotal }}</small>
          <strong>{{ formatMoney(spentAmount) }}</strong>
          <small>{{ text.kpis.spentPaid }}</small>
          <strong class="sale-summary-kpi-box__highlight">{{ formatMoney(spentPaidAmount) }}</strong>
        </div>
      </div>
      <div class="sale-summary-kpi-box">
        <div class="sale-summary-kpi-lines">
          <small>{{ text.kpis.available }}</small>
          <strong :class="availableAmountClass">{{ formatMoney(availableAmount) }}</strong>
          <small>{{ text.kpis.availableReal }}</small>
          <strong :class="availableRealAmountClass">{{ formatMoney(availableRealAmount) }}</strong>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import { SALES_TEXT } from '@/texts/catalog/sales';
import { formatCurrency } from '@/utils/numberFormat';

const EXPENSES_CARD_TEXT = SALES_TEXT.shared.summary.expensesCard;

export default {
  name: 'SaleSummaryExpensesProgressCard',
  props: {
    budgetAmount: {
      type: Number,
      default: 0,
    },
    spentAmount: {
      type: Number,
      default: 0,
    },
    spentPaidAmount: {
      type: Number,
      default: 0,
    },
    availableAmount: {
      type: Number,
      default: 0,
    },
    availableRealAmount: {
      type: Number,
      default: 0,
    },
    progressPercent: {
      type: Number,
      default: 0,
    },
    progressPercentRaw: {
      type: Number,
      default: 0,
    },
    paidProgressPercent: {
      type: Number,
      default: 0,
    },
    paidProgressPercentRaw: {
      type: Number,
      default: 0,
    },
    expenseStatus: {
      type: Object,
      default: () => ({ label: EXPENSES_CARD_TEXT.defaults.statusLabel, className: 'is-success' }),
    },
  },
  data() {
    return {
      text: EXPENSES_CARD_TEXT,
    };
  },
  computed: {
    totalProgressLabel() {
      return `${Number(this.progressPercentRaw || 0).toFixed(1)}%`;
    },
    paidProgressLabel() {
      return `${Number(this.paidProgressPercentRaw || 0).toFixed(1)}%`;
    },
    progressStyle() {
      return {
        width: `${Number(this.progressPercent || 0)}%`,
      };
    },
    paidProgressStyle() {
      return {
        width: `${Number(this.paidProgressPercent || 0)}%`,
      };
    },
    availableAmountClass() {
      if (Number(this.availableAmount || 0) < 0) {
        return 'sale-summary-kpi-box__warning';
      }

      return 'sale-summary-kpi-box__highlight';
    },
    availableRealAmountClass() {
      if (Number(this.availableRealAmount || 0) < 0) {
        return 'sale-summary-kpi-box__warning';
      }

      return 'sale-summary-kpi-box__highlight';
    },
  },
  methods: {
    formatMoney(value) {
      return formatCurrency(value || 0);
    },
  },
};
</script>

<style scoped>
.sale-summary-progress__head--secondary {
  margin-top: 10px;
}

.sale-summary-progress__fill--paid {
  background: linear-gradient(90deg, #0ea5e9 0%, #2563eb 100%);
}

.sale-summary-kpi-lines {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2px;
}

.sale-summary-kpi-lines small {
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: 10px;
  font-weight: 700;
  margin-top: 4px;
}

.sale-summary-kpi-lines small:first-child {
  margin-top: 0;
}

.sale-summary-kpi-lines strong {
  font-size: 20px;
  line-height: 1.15;
}

.sale-summary-kpi-box__warning {
  color: #b45309 !important;
}
</style>
